import { parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';

const isValidUUID = (value: string | undefined): boolean => {
    return z.uuid().safeParse(value).success;
};

const getValidFamilyId = (familyId: string | undefined): string | undefined => {
    return isValidUUID(familyId) ? familyId : undefined;
};

const getValidClusterId = (clusterId: string | undefined, familyId: string | undefined): string | undefined => {
    const validFamilyId = getValidFamilyId(familyId);

    return validFamilyId !== undefined && isValidUUID(clusterId) ? clusterId : undefined;
};

const getValidRoleId = (
    roleId: string | undefined,
    familyId: string | undefined,
    clusterId: string | undefined,
): string | undefined => {
    const validClusterId = getValidClusterId(clusterId, familyId);

    return validClusterId !== undefined && isValidUUID(roleId) ? roleId : undefined;
};

const getValidLevelId = (
    levelId: string | undefined,
    roleId: string | undefined,
    familyId: string | undefined,
    clusterId: string | undefined,
): string | undefined => {
    const validRoleId = getValidRoleId(roleId, familyId, clusterId);

    return validRoleId !== undefined && isValidUUID(levelId) ? levelId : undefined;
};

const selectionParsers = {
    cluster: parseAsString,
    family: parseAsString,
    level: parseAsString,
    role: parseAsString,
};

// Selection writes mirror user navigation steps; they must not pollute history.
const selectionOptions = { history: 'replace' } as const;

export type UrlSelectionIds = {
    readonly clusterId?: string;
    readonly familyId?: string;
    readonly levelId?: string;
    readonly roleId?: string;
};

export type ActiveSegment = 'job-cluster' | 'job-family' | 'job-level' | 'job-role';

export type UrlSelection = {
    readonly clearSelection: () => void;
    readonly isLastActiveSegment: (segment: ActiveSegment) => boolean;
    readonly selectedClusterId: string | undefined;
    readonly selectedFamilyId: string | undefined;
    readonly selectedLevelId: string | undefined;
    readonly selectedRoleId: string | undefined;
    readonly setSelectedClusterId: (clusterId: string | undefined) => void;
    readonly setSelectedFamilyId: (familyId: string | undefined) => void;
    readonly setSelectedLevelId: (levelId: string | undefined, parentRoleId?: string) => void;
    readonly setSelectedRoleId: (roleId: string | undefined) => void;
    readonly setSelection: (selection: UrlSelectionIds) => void;
};

// Board selection lives in the `family`/`cluster`/`role`/`level` search params;
// the URL is the single source of truth. Values are validated as a dependency
// chain at read time: a cluster without a valid family is treated as unset, etc.
export const useUrlSelection = (): UrlSelection => {
    const [params, setParams] = useQueryStates(selectionParsers, selectionOptions);

    const familyParam = params.family ?? undefined;
    const clusterParam = params.cluster ?? undefined;
    const roleParam = params.role ?? undefined;
    const levelParam = params.level ?? undefined;

    const selectedFamilyId = getValidFamilyId(familyParam);
    const selectedClusterId = getValidClusterId(clusterParam, familyParam);
    const selectedRoleId = getValidRoleId(roleParam, familyParam, clusterParam);
    const selectedLevelId = getValidLevelId(levelParam, roleParam, familyParam, clusterParam);

    const setSelectedFamilyId = useCallback(
        (familyId: string | undefined) => {
            void setParams({ cluster: null, family: familyId ?? null, level: null, role: null });
        },
        [setParams],
    );

    const setSelectedClusterId = useCallback(
        (clusterId: string | undefined) => {
            void setParams({ cluster: clusterId ?? null, level: null, role: null });
        },
        [setParams],
    );

    const setSelectedRoleId = useCallback(
        (roleId: string | undefined) => {
            void setParams({ level: null, role: roleId ?? null });
        },
        [setParams],
    );

    const setSelectedLevelId = useCallback(
        (levelId: string | undefined, parentRoleId?: string) => {
            void setParams((previous) => {
                const roleToUse = parentRoleId ?? previous.role ?? undefined;

                if (roleToUse === undefined) {
                    // A level cannot be selected without a role; keep the URL unchanged.
                    return {};
                }

                return { level: levelId ?? null, role: roleToUse };
            });
        },
        [setParams],
    );

    const setSelection = useCallback(
        ({ clusterId, familyId, levelId, roleId }: UrlSelectionIds) => {
            void setParams({
                cluster: clusterId ?? null,
                family: familyId ?? null,
                level: levelId ?? null,
                role: roleId ?? null,
            });
        },
        [setParams],
    );

    const clearSelection = useCallback(() => {
        void setParams({ cluster: null, family: null, level: null, role: null });
    }, [setParams]);

    const isLastActiveSegment = useCallback(
        (segment: ActiveSegment): boolean => {
            if (selectedLevelId !== undefined) {
                return segment === 'job-level';
            }

            if (selectedRoleId !== undefined) {
                return segment === 'job-role';
            }

            if (selectedClusterId !== undefined) {
                return segment === 'job-cluster';
            }

            if (selectedFamilyId !== undefined) {
                return segment === 'job-family';
            }

            return false;
        },
        [selectedClusterId, selectedFamilyId, selectedLevelId, selectedRoleId],
    );

    return useMemo(
        () => ({
            clearSelection,
            isLastActiveSegment,
            selectedClusterId,
            selectedFamilyId,
            selectedLevelId,
            selectedRoleId,
            setSelectedClusterId,
            setSelectedFamilyId,
            setSelectedLevelId,
            setSelectedRoleId,
            setSelection,
        }),
        [
            clearSelection,
            isLastActiveSegment,
            selectedClusterId,
            selectedFamilyId,
            selectedLevelId,
            selectedRoleId,
            setSelectedClusterId,
            setSelectedFamilyId,
            setSelectedLevelId,
            setSelectedRoleId,
            setSelection,
        ],
    );
};
